import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AccessDeniedNotice } from '../../../shared/AccessDeniedNotice';
import { EmptyState } from '../../../shared/EmptyState';
import { RetryableError } from '../../../shared/RetryableError';
import { formatReportDate } from '../../reports/formatting';
import { peopleGateway } from '../../people';
import type { DepartmentOption } from '../../people/types';
import { ArchiveRoleDialog } from '../components/ArchiveRoleDialog';
import { AssignEmployeesDialog } from '../components/AssignEmployeesDialog';
import { DuplicateRoleDialog } from '../components/DuplicateRoleDialog';
import { RoleStatusBadge } from '../components/RoleStatusBadge';
import { RoleWizardDialog, type WizardMode, type WizardOutcome } from '../components/RoleWizardDialog';
import { RowActionsMenu, type RowAction } from '../components/RowActionsMenu';
import { jobRoleGateway } from '../index';
import { isForbidden, jobRoleErrorMessage } from '../jobRoleErrors';
import { benchmarkStatusLabel, formatBandRange } from '../payBand';
import {
  CAREER_LEVEL_SUGGESTIONS, JOB_FAMILY_SUGGESTIONS, JobRoleStatus, TYPICAL_PAY_TYPE_PRESETS, jobRoleStatusLabels,
  type JobRoleDetail, type JobRoleListItem, type JobRoleSort, type Paged, type PayBasis,
} from '../types';
import '../job-roles.css';

/** "$24.00 – $32.00 / hr", broken (if the column is narrow) only between the amounts and the unit, never inside an amount. */
function BandRange({ band }: { band: { basis: PayBasis; minimum: number; maximum: number } }) {
  const text = formatBandRange(band);
  const cut = text.lastIndexOf(' / ');
  return <><span className="jr-nowrap">{text.slice(0, cut)}</span> <span className="jr-nowrap">{text.slice(cut + 1)}</span></>;
}

const SEARCH_DEBOUNCE_MS = 300;
const PAGE_SIZES = [10, 25, 50];

const SORTS: { value: JobRoleSort; label: string }[] = [
  { value: 'title', label: 'Role (A to Z)' },
  { value: '-title', label: 'Role (Z to A)' },
  { value: 'code', label: 'Internal code' },
  { value: 'department', label: 'Department' },
  { value: 'family', label: 'Job family' },
  { value: 'level', label: 'Level' },
  { value: 'paytype', label: 'Pay type' },
  { value: 'status', label: 'Status' },
  { value: '-effective', label: 'Newest effective date' },
];

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; data: Paged<JobRoleListItem> }
  | { kind: 'forbidden' }
  | { kind: 'error'; message: string };

type Dialog =
  | { kind: 'wizard'; mode: WizardMode; roleId?: string }
  | { kind: 'duplicate'; role: JobRoleListItem }
  | { kind: 'archive'; role: JobRoleListItem }
  | { kind: 'assign'; role: JobRoleListItem };

interface Notice {
  text: string;
  roleId?: string;
}

/**
 * Job Role Library (PR 1): the company's job roles. A job role says what someone does; it is NOT a system access role and never
 * grants a permission. Every permission decision is the server's: a 403 on a read shows the no-access notice, and a 403 on a write
 * turns the page read-only for the rest of the visit (no write action is offered again, and the page says why).
 */
export function RolesPage() {
  const navigate = useNavigate();
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [reloadNonce, setReloadNonce] = useState(0);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [jobFamily, setJobFamily] = useState('');
  const [careerLevel, setCareerLevel] = useState('');
  const [status, setStatus] = useState('');
  const [payType, setPayType] = useState('');
  const [sort, setSort] = useState<JobRoleSort>('title');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]!);
  const [departments, setDepartments] = useState<DepartmentOption[]>([]);
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [readOnly, setReadOnly] = useState(false);
  const [busyRoleId, setBusyRoleId] = useState<string | null>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  // Department names for the filter and the dialogs. A failed read just leaves the lists short.
  useEffect(() => {
    let cancelled = false;
    peopleGateway.listDepartments().then((d) => { if (!cancelled) setDepartments(d.filter((x) => x.isActive)); }).catch(() => undefined);
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setState((prev) => (prev.kind === 'ready' ? prev : { kind: 'loading' }));
    jobRoleGateway
      .listRoles({
        search: debouncedSearch || undefined,
        departmentId: departmentId || undefined,
        jobFamily: jobFamily || undefined,
        careerLevel: careerLevel || undefined,
        status: status === '' ? undefined : Number(status),
        payType: payType === '' ? undefined : payType,
        sort,
        page,
        pageSize,
      }, controller.signal)
      .then((data) => { if (!controller.signal.aborted) setState({ kind: 'ready', data }); })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setState(isForbidden(err) ? { kind: 'forbidden' } : { kind: 'error', message: jobRoleErrorMessage(err, 'Could not load roles.') });
      });
    return () => controller.abort();
  }, [debouncedSearch, departmentId, jobFamily, careerLevel, status, payType, sort, page, pageSize, reloadNonce]);

  const reload = useCallback(() => setReloadNonce((n) => n + 1), []);
  const markReadOnly = useCallback(() => setReadOnly(true), []);

  const filtersActive = debouncedSearch !== '' || departmentId !== '' || jobFamily !== '' || careerLevel !== '' || status !== '' || payType !== '';

  function clearFilters() {
    setSearch(''); setDebouncedSearch(''); setDepartmentId(''); setJobFamily(''); setCareerLevel(''); setStatus(''); setPayType(''); setPage(1);
  }

  function wizardDone(role: JobRoleDetail, outcome: WizardOutcome, note?: string) {
    setDialog(null);
    setNotice({
      text: `${outcome === 'activated' ? `${role.title} is now active and can be assigned to employees.` : `${role.title} was saved as a draft.`}${note ? ` ${note}` : ''}`,
      roleId: role.id,
    });
    reload();
  }

  async function runAction(role: JobRoleListItem, run: () => Promise<unknown>, success: string, failure: string) {
    setBusyRoleId(role.id);
    setActionError(null);
    setNotice(null);
    try {
      await run();
      setNotice({ text: success, roleId: role.id });
      reload();
    } catch (err) {
      if (isForbidden(err)) markReadOnly();
      setActionError(jobRoleErrorMessage(err, failure));
    } finally {
      setBusyRoleId(null);
    }
  }

  function actionsFor(role: JobRoleListItem): RowAction[] {
    const actions: RowAction[] = [{ key: 'view', label: 'View', onSelect: () => navigate(`/people/roles/${role.id}`) }];
    const isDraft = role.status === JobRoleStatus.Draft;
    const isActive = role.status === JobRoleStatus.Active;
    const isArchived = role.status === JobRoleStatus.Archived;
    if (!readOnly) {
      if (isDraft || (isActive && role.hasDraft)) actions.push({ key: 'edit', label: 'Edit', onSelect: () => setDialog({ kind: 'wizard', mode: 'edit', roleId: role.id }) });
      actions.push({ key: 'duplicate', label: 'Duplicate', onSelect: () => setDialog({ kind: 'duplicate', role }) });
      if (isActive && !role.hasDraft) {
        actions.push({
          key: 'version', label: 'Create New Version',
          onSelect: () => void runAction(role, async () => { await jobRoleGateway.createVersion(role.id, role.concurrencyVersion); }, `A new draft version of ${role.title} is open. View the role to edit and activate it.`, 'Could not create a new version.'),
        });
      }
      if (isActive) actions.push({ key: 'assign', label: 'Assign Employees', onSelect: () => setDialog({ kind: 'assign', role }) });
    }
    if (!isDraft) actions.push({ key: 'employees', label: 'View Assigned Employees', onSelect: () => navigate(`/people/roles/${role.id}?tab=employees`) });
    if (!readOnly) {
      if (isDraft || isActive) actions.push({ key: 'archive', label: 'Archive', danger: true, onSelect: () => setDialog({ kind: 'archive', role }) });
      if (isArchived && role.currentVersionNumber !== null) {
        actions.push({
          key: 'restore', label: 'Restore',
          onSelect: () => void runAction(role, async () => { await jobRoleGateway.restoreRole(role.id, role.concurrencyVersion); }, `${role.title} is active again.`, 'Could not restore this role.'),
        });
      }
    }
    return actions;
  }

  const data = state.kind === 'ready' ? state.data : null;
  const totalPages = data ? Math.max(1, Math.ceil(data.totalCount / data.pageSize)) : 1;
  const firstIndex = data ? (data.page - 1) * data.pageSize : 0;
  const familyOptions = useMemo(() => [...new Set([...JOB_FAMILY_SUGGESTIONS, ...(jobFamily ? [jobFamily] : [])])], [jobFamily]);
  const levelOptions = useMemo(() => [...new Set([...CAREER_LEVEL_SUGGESTIONS, ...(careerLevel ? [careerLevel] : [])])], [careerLevel]);
  const payTypeOptions = useMemo(() => [...new Set([...TYPICAL_PAY_TYPE_PRESETS, ...(payType ? [payType] : [])])], [payType]);

  return (
    <section className="card card--wide jr-page">
      <div className="section-header">
        <h2 ref={headingRef} tabIndex={-1}>Roles</h2>
        {!readOnly && state.kind !== 'forbidden' && (
          <div className="jr-actions">
            <button type="button" className="jr-btn jr-btn--secondary" onClick={() => setDialog({ kind: 'wizard', mode: 'template' })}>Use EPN Template</button>
            <button type="button" className="jr-btn jr-btn--primary" onClick={() => setDialog({ kind: 'wizard', mode: 'blank' })}>
              <span aria-hidden="true">+ </span>Add Role
            </button>
          </div>
        )}
      </div>
      <p className="muted jr-explainer">
        Job roles describe what people do at your company. System access roles, which control what people can do in EPN, are managed in{' '}
        <Link to="/users-permissions">Users &amp; Permissions</Link>.
      </p>

      {readOnly && (
        <p className="jr-readonly" role="status" data-testid="read-only-notice">
          <span aria-hidden="true">{'🔒'} </span>
          You can view roles but not change them. Ask a company administrator if you need to add, edit, archive or assign roles.
        </p>
      )}
      {notice && (
        <p className="jr-notice jr-notice--ok" role="status">
          <span aria-hidden="true">{'✓'} </span>
          {notice.text}
          {notice.roleId && <>{' '}<Link to={`/people/roles/${notice.roleId}`}>Open role</Link></>}
          {' '}
          <button type="button" className="link" onClick={() => { setNotice(null); headingRef.current?.focus(); }}>Dismiss</button>
        </p>
      )}
      {actionError && <p className="jr-error jr-error--block" role="alert">{actionError}</p>}

      {state.kind === 'forbidden' ? (
        <AccessDeniedNotice requiredPermissionLabel="permission to view roles (roles.read)" />
      ) : (
        <>
          <div className="jr-toolbar">
            <div className="jr-field jr-field--search">
              <label htmlFor="jr-search">Search roles</label>
              <input id="jr-search" type="search" placeholder="Title, code, family or department" value={search} onChange={(e) => setSearch(e.target.value)} autoComplete="off" />
            </div>
            <div className="jr-filters" role="group" aria-label="Filters">
              <div className="jr-field">
                <label htmlFor="jr-f-dept">Department</label>
                <select id="jr-f-dept" value={departmentId} onChange={(e) => { setDepartmentId(e.target.value); setPage(1); }}>
                  <option value="">All departments</option>
                  {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </div>
              <div className="jr-field">
                <label htmlFor="jr-f-family">Job family</label>
                <select id="jr-f-family" value={jobFamily} onChange={(e) => { setJobFamily(e.target.value); setPage(1); }}>
                  <option value="">All families</option>
                  {familyOptions.map((f) => <option key={f} value={f}>{f}</option>)}
                </select>
              </div>
              <div className="jr-field">
                <label htmlFor="jr-f-level">Level</label>
                <select id="jr-f-level" value={careerLevel} onChange={(e) => { setCareerLevel(e.target.value); setPage(1); }}>
                  <option value="">All levels</option>
                  {levelOptions.map((l) => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
              <div className="jr-field">
                <label htmlFor="jr-f-status">Status</label>
                <select id="jr-f-status" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
                  <option value="">All statuses</option>
                  {Object.entries(jobRoleStatusLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
              </div>
              <div className="jr-field">
                <label htmlFor="jr-f-pay">Pay type</label>
                <select id="jr-f-pay" value={payType} onChange={(e) => { setPayType(e.target.value); setPage(1); }}>
                  <option value="">All pay types</option>
                  {payTypeOptions.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div className="jr-field">
                <label htmlFor="jr-sort">Sort by</label>
                <select id="jr-sort" value={sort} onChange={(e) => { setSort(e.target.value as JobRoleSort); setPage(1); }}>
                  {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
              {filtersActive && <button type="button" className="link" onClick={clearFilters}>Clear filters</button>}
            </div>
          </div>

          {state.kind === 'loading' && <p className="muted" role="status">Loading roles{'…'}</p>}
          {state.kind === 'error' && <RetryableError message={state.message} onRetry={reload} />}

          {data && data.totalCount === 0 && !filtersActive && (
            <EmptyState
              message="No roles yet. Start from an EPN template, or add your own role. Employees need an active role before you can add them."
              action={readOnly ? undefined : (
                <div className="jr-actions">
                  <button type="button" className="jr-btn jr-btn--secondary" onClick={() => setDialog({ kind: 'wizard', mode: 'template' })}>Use EPN Template</button>
                  <button type="button" className="jr-btn jr-btn--primary" onClick={() => setDialog({ kind: 'wizard', mode: 'blank' })}>Add Role</button>
                </div>
              )}
            />
          )}
          {data && data.totalCount === 0 && filtersActive && (
            <EmptyState message="No roles match your search or filters." action={<button type="button" className="jr-btn jr-btn--secondary" onClick={clearFilters}>Clear filters</button>} />
          )}

          {data && data.totalCount > 0 && (
            <>
              <p className="muted" aria-live="polite" data-testid="roles-count">
                Showing {firstIndex + 1}{'–'}{firstIndex + data.items.length} of {data.totalCount} {data.totalCount === 1 ? 'role' : 'roles'}
              </p>
              <div className="jr-table-scroll" role="region" aria-label="Job roles table" tabIndex={0}>
                <table className="jr-table jr-table--actions">
                  <caption className="jr-sr-only">Job roles for this company</caption>
                  <thead>
                    <tr>
                      <th scope="col">Role</th>
                      <th scope="col">Internal Code</th>
                      <th scope="col">Department</th>
                      <th scope="col">Job Family</th>
                      <th scope="col">Level</th>
                      <th scope="col" className="num">Employees Assigned</th>
                      <th scope="col">Pay Type</th>
                      <th scope="col">Employer Pay Band</th>
                      <th scope="col">Benchmark Status</th>
                      <th scope="col">Status</th>
                      <th scope="col">Effective Date</th>
                      <th scope="col">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((role) => {
                      const dash = <span className="muted" title="Not set">{'—'}</span>;
                      return (
                        <tr key={role.id} aria-busy={busyRoleId === role.id || undefined}>
                          <th scope="row" className="jr-table__title"><Link to={`/people/roles/${role.id}`}>{role.title}</Link></th>
                          <td className="jr-nowrap">{role.roleCode ?? dash}</td>
                          <td>{role.departmentName ?? dash}</td>
                          <td>{role.jobFamily ?? dash}</td>
                          <td>{role.careerLevel ?? dash}</td>
                          <td className="num">{role.employeesAssigned}</td>
                          <td>{role.typicalPayType ?? dash}</td>
                          <td data-testid="pay-band-cell">
                            {role.employerPayBand ? <BandRange band={role.employerPayBand} /> : <span className="muted">Not set</span>}
                          </td>
                          <td data-testid="benchmark-status-cell">
                            {role.benchmarkStatus === 'NotPermitted'
                              ? <><span className="muted" aria-hidden="true">{'—'}</span><span className="jr-sr-only">Not available to you</span></>
                              : benchmarkStatusLabel(role.benchmarkStatus)}
                          </td>
                          <td><RoleStatusBadge status={role.status} /></td>
                          <td className="jr-nowrap">{role.effectiveFromUtc ? formatReportDate(role.effectiveFromUtc) : dash}</td>
                          <td><RowActionsMenu label={`Actions for ${role.title}`} actions={actionsFor(role)} /></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="jr-pager">
                <label className="jr-pager__size">
                  Rows per page{' '}
                  <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}>
                    {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                </label>
                <nav aria-label="Pagination" className="jr-pager__nav">
                  <button type="button" className="jr-btn jr-btn--secondary jr-btn--small" disabled={data.page <= 1} onClick={() => setPage(data.page - 1)}>Previous</button>
                  <span>Page {data.page} of {totalPages}</span>
                  <button type="button" className="jr-btn jr-btn--secondary jr-btn--small" disabled={data.page >= totalPages} onClick={() => setPage(data.page + 1)}>Next</button>
                </nav>
              </div>
            </>
          )}
        </>
      )}

      {dialog?.kind === 'wizard' && (
        <RoleWizardDialog
          mode={dialog.mode}
          {...(dialog.roleId ? { roleId: dialog.roleId } : {})}
          departments={departments}
          onClose={() => setDialog(null)}
          onDone={wizardDone}
          onChanged={reload}
          onForbidden={markReadOnly}
        />
      )}
      {dialog?.kind === 'duplicate' && (
        <DuplicateRoleDialog
          role={dialog.role}
          onClose={() => setDialog(null)}
          onForbidden={markReadOnly}
          onDuplicated={(created) => { setDialog(null); navigate(`/people/roles/${created.id}`); }}
        />
      )}
      {dialog?.kind === 'archive' && (
        <ArchiveRoleDialog
          role={dialog.role}
          onClose={() => setDialog(null)}
          onForbidden={markReadOnly}
          onArchived={() => {
            const title = dialog.role.title;
            setDialog(null);
            setNotice({ text: `${title} was archived. It was not deleted, and existing assignments are unchanged.`, roleId: dialog.role.id });
            reload();
          }}
        />
      )}
      {dialog?.kind === 'assign' && (
        <AssignEmployeesDialog
          role={dialog.role}
          onClose={() => setDialog(null)}
          onForbidden={markReadOnly}
          onAssigned={(count) => {
            setNotice({ text: `${count} ${count === 1 ? 'employee was' : 'employees were'} assigned to ${dialog.role.title}.`, roleId: dialog.role.id });
            reload();
          }}
        />
      )}
    </section>
  );
}
